'use client';

import { useTranslations } from 'next-intl';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import { BaseLineSearch } from '@/app/components/icons/BaseLineSearch';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { downloadFile } from '@/app/utils/client';
import { ARTIST_POSTER_BG_URL } from './constants';
import type { Medal } from './types';

interface ArtistPosterProps {
  medal: Medal;
  owner?: {
    avatar?: string;
    nickname?: string;
  } | null;
  onDownload?: () => void;
}

export interface ArtistPosterRef {
  download: () => Promise<void>;
}

export const ArtistPoster = forwardRef<ArtistPosterRef, ArtistPosterProps>(({ medal, owner, onDownload }, ref) => {
  const t = useTranslations();
  const posterRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!posterRef.current) {
      toast({
        type: 'error',
        message: t('popup.toast.downloadMedalFailed'),
      });
      return;
    }

    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(posterRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false,
        width: posterRef.current.offsetWidth,
        height: posterRef.current.offsetHeight,
      });

      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Failed to convert canvas to blob'));
              return;
            }
            resolve(blob);
          },
          'image/png',
          0.95,
        );
      });

      const fileName = `vidu-${medal.user_medal_type}_${owner?.nickname}.png`;

      const url = URL.createObjectURL(blob);
      downloadFile(url, fileName, { autoRevokeBlobUrl: true });

      toast({
        type: 'success',
        message: t('popup.toast.downloadMedal'),
      });

      onDownload?.();
    } catch (error) {
      console.error('Failed to download poster:', error);
      toast({
        type: 'error',
        message: t('popup.toast.downloadMedalFailed'),
      });
    }
  };

  useImperativeHandle(ref, () => ({
    download: handleDownload,
  }));

  return (
    <div
      ref={posterRef}
      className="relative flex h-[1200px] w-[900px] flex-col overflow-hidden bg-[#16151B] backdrop-blur-[50px]"
    >
      <img
        src={ARTIST_POSTER_BG_URL[medal.user_medal_type as keyof typeof ARTIST_POSTER_BG_URL]?.[SITE]}
        alt=""
        className="absolute top-0 right-0 left-0 w-full object-cover"
      />

      <div className="absolute right-6 bottom-16 left-6 flex min-h-[366px] flex-col items-center border-system-white10 border-x border-b pt-11">
        <div className="flex flex-col items-center">
          {owner?.avatar && (
            <img src={owner.avatar} alt={owner.nickname} className="size-[140px] rounded-full object-cover" />
          )}
          {owner?.nickname && (
            <div className="mt-[2px] font-semibold text-[40px] text-white leading-[1.4]">{owner.nickname}</div>
          )}
        </div>

        {medal.medal_achievement_condition && (
          <div className="max-w-[533px] pt-1 pb-16 text-center text-system-white48 text-xl">
            {medal.medal_achievement_condition}
          </div>
        )}

        <div className="-translate-x-1/2 absolute bottom-0 left-1/2 z-20 translate-y-1/2 bg-[#16151B] px-1.5">
          <div className="flex h-12 items-center gap-4 overflow-hidden rounded-full bg-system-white08 pr-8 pl-6">
            <BaseLineSearch className="size-6 flex-shrink-0 text-white" />
            <div className="h-6 w-[1px] bg-system-white12" />
            {/* 使用leading-normal让下载的网站域名垂直居中对齐 */}
            <div className="h-12 text-system-white64 text-xl leading-normal">{window.location.host}</div>
          </div>
        </div>
      </div>
    </div>
  );
});
